import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { Switch } from 'cpool-components/input/Switch';
import { SwitchSize } from 'cpool-components/input/Switch/types';
import { AGGREGATE_NAMES, LOAN_REQUEST_RESOLVERS, USER_COMMANDS, USER_LEAD_CARRYING_ROLES } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import useCallbackWithErrorHandler from '../hooks/useCallbackWithErrorHandler.tsx';
import useUserInfo from '../hooks/useUserInfo.tsx';
import { HeaderButton } from './Header/HeaderButton.tsx';
import { useUserData } from './Header/menu/hooks/useUserData.ts';
import { APICommunicator } from '~/api/APICommunicator.ts';
import { EndRepresentationDialog } from '~/components/Dialogs/EndRepresentationDialog';
import { useEndRepresentationMutation } from '~/components/Dialogs/hooks/useEndRepresentationMutation';
import RequiredRoles from '~/components/RequiredRoles.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { setLeadTrackStatus } from '~/store/leadTrackStore.ts';
import { setQueryPosition } from '~/store/queryPositionStore.ts';
import { queryKeys } from '~/utils/queryKeys.ts';
import { getActiveEndRepresentation } from '~/utils/representation';

const LeadTrack = () => {
    const { t } = useTranslation();
    const userInfo = useUserInfo();
    const { data: user } = useUserData(userInfo?.id);
    const queryClient = useQueryClient();
    const { permissions } = useAclPermissions();
    const [assignable, setAssignable] = useState(user?.assignable ?? false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const disabled = assignable
        ? !permissions.commands[AGGREGATE_NAMES.USER].revokeLimit
        : !permissions.commands[AGGREGATE_NAMES.USER].limit;

    const activeRepresentation = user ? getActiveEndRepresentation(user) : undefined;
    const appointerName = user ? `${user.firstName} ${user.lastName}` : undefined;

    const { isPending, mutate: endRepresentation } = useEndRepresentationMutation({
        onSuccess: () => {
            setDialogOpen(false);
            setAssignable(true);
            setLeadTrackStatus(true);
        },
        reactivateLeadTrack: true,
        userId: userInfo?.id ?? '',
    });

    const leadTrackCheckedChange = useCallbackWithErrorHandler(
        async (checked: boolean) => {
            if (userInfo === null) return;

            if (!checked && activeRepresentation) {
                setDialogOpen(true);
                return;
            }

            const { position } = await APICommunicator.sendCommand({
                aggregateId: userInfo.id,
                aggregateName: AGGREGATE_NAMES.USER,
                commandName: checked ? USER_COMMANDS.limit : USER_COMMANDS.revokeLimit,
                payload: {
                    aggregateId: userInfo.id,
                },
            });
            setAssignable(!assignable);
            setLeadTrackStatus(!assignable);

            const queryKey = queryKeys.getUser(userInfo.id);
            const queryKeyGetLeadTrackStatus = queryKeys.getLeadTrackStatus(userInfo.id);
            if (position) {
                setQueryPosition(JSON.stringify(queryKey), position);
                setQueryPosition(JSON.stringify(queryKeyGetLeadTrackStatus), position);
            }
            if (!checked) {
                // TODO: temporary fix we need to be able to react on event chains
                setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: queryKeys.getSummary() });
                    queryClient.invalidateQueries({
                        queryKey: [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getCounts],
                    });
                }, 300);
            }
            await queryClient.invalidateQueries({ queryKey });
            await queryClient.invalidateQueries({ queryKey: queryKeyGetLeadTrackStatus });
        },
        [activeRepresentation, assignable, queryClient, setAssignable, userInfo],
    );

    useEffect(() => {
        setAssignable(user?.assignable ?? false);
        setLeadTrackStatus(user?.assignable ?? false);
    }, [user, setAssignable]);

    return (
        <RequiredRoles requiredRoles={[...USER_LEAD_CARRYING_ROLES]}>
            <HeaderButton
                variant={ButtonVariant.SECONDARY}
                onClick={() => leadTrackCheckedChange(assignable)}
                disabled={disabled}
            >
                <div className="flex items-center justify-center gap-2">
                    <Switch
                        id="lead-track"
                        checked={assignable}
                        eventStopPropagation
                        size={SwitchSize.SM}
                        onChange={() => leadTrackCheckedChange(assignable)}
                    />
                    {t('TOP_HEADER.LEAD_TRACK')}
                </div>
            </HeaderButton>

            {activeRepresentation ? (
                <EndRepresentationDialog
                    appointerName={appointerName}
                    isPending={isPending}
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onConfirm={endRepresentation}
                    representation={activeRepresentation}
                />
            ) : null}
        </RequiredRoles>
    );
};

export default LeadTrack;
